import { Clock, CreditCard, Lock, Mail, Scale, Sparkles } from 'lucide-react';
import React, { useState } from 'react';
import { useProfile } from 'src/context/ProfileContext';
import { useSubscription } from 'src/context/SubscriptionContext';
import { subscriptionService } from 'src/services/subscriptionService';
import BrandLogo from 'src/shared/components/BrandLogo';
import { isValidStripeUrl } from 'src/utils/security';
import { getSupabase } from 'src/utils/supabase';

const SECURITY_EMAIL = 'security@tropel.ai';
const HOURS_SAVED_PER_TASK = 6;

function TrialExpiredScreen() {
  const { profile } = useProfile();
  const { subscription, usage } = useSubscription();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const firstName = profile?.first_name || '';
  const currency = subscription?.currency || 'usd';
  const isCAD = currency === 'cad';

  const researchCount = usage.find((u) => u.feature_key === 'research')?.count || 0;
  const reviewCount = usage.find((u) => u.feature_key === 'contract_reviews')?.count || 0;
  const uploadCount = usage.find((u) => u.feature_key === 'document_uploads')?.count || 0;
  const totalTasks = researchCount + reviewCount;
  const hoursSaved = totalTasks * HOURS_SAVED_PER_TASK;

  const trialStart = subscription?.trial_start;
  const trialEnd = subscription?.trial_end;
  const trialDays =
    trialStart && trialEnd
      ? Math.ceil((new Date(trialEnd).getTime() - new Date(trialStart).getTime()) / (1000 * 60 * 60 * 24))
      : 30;

  const handleContinue = async () => {
    if (!selectedPlan) return;
    setError(null);
    setLoading(true);
    try {
      const plans = await subscriptionService.getPlans();
      const plan = plans.find((p) => p.name === selectedPlan);
      if (!plan) {
        setLoading(false);
        return;
      }
      const url = await subscriptionService.createCheckoutSession(plan.id, currency);
      if (!isValidStripeUrl(url)) {
        setLoading(false);
        return;
      }
      window.location.href = url;
    } catch {
      setError('Unable to connect to billing. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
      <div className="w-20 h-20 rounded-full bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center mb-6 ring-1 ring-amber-200/60 dark:ring-amber-800/40">
        <Clock className="w-9 h-9 text-amber-400 dark:text-amber-500" strokeWidth={1.5} />
      </div>

      <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100 mb-3 tracking-tight">
        {firstName ? `${firstName}, your trial has ended` : 'Your trial has ended'}
      </h1>

      <p className="text-base text-slate-500 dark:text-slate-400 text-center leading-relaxed max-w-sm mb-8">
        You made great use of your {trialDays}-day free trial. Choose a plan to keep your work and continue saving time.
      </p>

      {(totalTasks > 0 || uploadCount > 0) && (
        <div className="w-full max-w-md bg-slate-50 dark:bg-slate-800/50 rounded-xl p-5 mb-8 ring-1 ring-slate-200/60 dark:ring-slate-700/40">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-4">
            Your trial in numbers
          </p>
          <div className="grid grid-cols-3 gap-4 text-center">
            {researchCount > 0 && (
              <div>
                <div className="flex items-center justify-center mb-1.5">
                  <Scale className="w-4 h-4 text-blue-500" />
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{researchCount}</p>
                <p className="text-xs text-slate-400">{researchCount === 1 ? 'Research' : 'Researches'}</p>
              </div>
            )}
            {reviewCount > 0 && (
              <div>
                <div className="flex items-center justify-center mb-1.5">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{reviewCount}</p>
                <p className="text-xs text-slate-400">{reviewCount === 1 ? 'Review' : 'Reviews'}</p>
              </div>
            )}
            {hoursSaved > 0 && (
              <div>
                <div className="flex items-center justify-center mb-1.5">
                  <Clock className="w-4 h-4 text-emerald-500" />
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{hoursSaved}h</p>
                <p className="text-xs text-slate-400">Time Saved</p>
              </div>
            )}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

      <div className="w-full max-w-md grid grid-cols-2 gap-3 mb-5">
        <button
          onClick={() => setSelectedPlan('starter')}
          disabled={loading}
          className={`flex flex-col items-center gap-1.5 px-5 py-5 rounded-xl transition-all duration-200 disabled:opacity-60 ${
            selectedPlan === 'starter'
              ? 'border-2 border-primary bg-primary/5 dark:bg-primary/10 ring-2 ring-primary/20'
              : 'border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
          }`}
        >
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Starter</span>
          <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
            {isCAD ? '$249' : '$199'}
            <span className="text-xs font-normal text-slate-400">/mo {isCAD ? 'CAD' : 'USD'}</span>
          </span>
          <span className="text-xs text-slate-400">Usage-based limits</span>
        </button>

        <button
          onClick={() => setSelectedPlan('professional')}
          disabled={loading}
          className={`relative flex flex-col items-center gap-1.5 px-5 py-5 rounded-xl transition-all duration-200 disabled:opacity-60 ${
            selectedPlan === 'professional'
              ? 'border-2 border-primary bg-primary/5 dark:bg-primary/10 ring-2 ring-primary/20'
              : 'border-2 border-primary/30 bg-white dark:bg-slate-800 hover:border-primary/50'
          }`}
        >
          <span className="absolute -top-2.5 px-2.5 py-0.5 rounded-full bg-primary text-white text-[10px] font-semibold uppercase tracking-wide">
            Recommended
          </span>
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Professional</span>
          <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
            {isCAD ? '$349' : '$299'}
            <span className="text-xs font-normal text-slate-400">/mo {isCAD ? 'CAD' : 'USD'}</span>
          </span>
          <span className="text-xs text-slate-400">Unlimited everything</span>
        </button>
      </div>

      <button
        onClick={handleContinue}
        disabled={!selectedPlan || loading}
        className="inline-flex items-center gap-2.5 px-10 py-4 rounded-xl bg-primary text-white font-medium text-base tracking-wide hover:bg-primary-hover transition-colors duration-200 shadow-sm mb-3 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? 'Connecting...' : 'Continue to Checkout'}
      </button>

      <p className="text-sm text-slate-400 dark:text-slate-500">Cancel anytime</p>
    </>
  );
}

function PendingPaymentScreen() {
  const { subscription } = useSubscription();
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCompletePayment = async () => {
    setError(null);
    setUpgrading(true);
    try {
      const planId = subscription?.plan_id;
      const currency = subscription?.currency || 'usd';
      if (!planId) {
        setUpgrading(false);
        return;
      }
      const url = await subscriptionService.createCheckoutSession(planId, currency);
      if (!isValidStripeUrl(url)) {
        setUpgrading(false);
        return;
      }
      window.location.href = url;
    } catch {
      setError('Unable to connect to billing. Please try again.');
      setUpgrading(false);
    }
  };

  return (
    <>
      <div className="w-20 h-20 rounded-full bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center mb-6 ring-1 ring-blue-200/60 dark:ring-blue-800/40">
        <CreditCard className="w-9 h-9 text-blue-400 dark:text-blue-500" strokeWidth={1.5} />
      </div>

      <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100 mb-3 tracking-tight">
        Complete your setup
      </h1>

      <p className="text-base text-slate-500 dark:text-slate-400 text-center leading-relaxed max-w-sm mb-4">
        Your workspace is ready, but we need your payment details to activate your plan.
      </p>
      <p className="text-sm text-slate-400 dark:text-slate-500 text-center leading-relaxed max-w-sm mb-10">
        This only takes a moment. Your account will be fully active as soon as payment is confirmed.
      </p>

      {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

      <button
        onClick={handleCompletePayment}
        disabled={upgrading}
        className="inline-flex items-center gap-2.5 px-10 py-4 rounded-xl bg-primary text-white font-medium text-base tracking-wide hover:bg-primary-hover transition-colors duration-200 shadow-sm mb-3 disabled:opacity-60"
      >
        <CreditCard className="w-5 h-5" />
        {upgrading ? 'Connecting...' : 'Complete Payment'}
      </button>

      <p className="text-sm text-slate-400 dark:text-slate-500">Secure checkout powered by Stripe</p>
    </>
  );
}

function SecurityLockedScreen() {
  const { profile } = useProfile();
  const { subscription, usage } = useSubscription();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleContactSecurity = async () => {
    if (sending || sent) return;
    setSending(true);

    const usageSummary = usage
      .filter((u) => u.count > 0)
      .map((u) => `${u.feature_key}: ${u.count}`)
      .join(', ');

    const details = [
      `Profile ID: ${profile?.id ?? 'unknown'}`,
      `Name: ${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim(),
      `Email: ${profile?.email ?? 'unknown'}`,
      `Firm: ${profile?.firm_name ?? 'N/A'}`,
      `Jurisdiction: ${profile?.jurisdiction ?? 'N/A'}`,
      `Subscription Status: ${subscription?.status ?? 'unknown'}`,
      `Plan: ${subscription?.subscription_plans?.name ?? 'unknown'}`,
      `Trial End: ${subscription?.trial_end ?? 'N/A'}`,
      `Usage This Period: ${usageSummary || 'none'}`,
    ].join('\n');

    const textBody = `Locked Account Review Request\n\n${details}\n\nThis email was auto-generated from the locked account screen.`;

    try {
      const supabase = await getSupabase();
      await supabase.functions.invoke('send-contact-email', {
        body: {
          name: `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim() || 'Locked User',
          email: profile?.email ?? 'unknown@tropel.ai',
          subject: `Locked Account Review - ${profile?.email ?? 'Unknown'}`,
          message: textBody,
        },
      });
      setSent(true);
    } catch {
      const subject = encodeURIComponent(`Locked Account Review - ${profile?.email ?? 'Unknown'}`);
      const body = encodeURIComponent(textBody);
      window.location.href = `mailto:${SECURITY_EMAIL}?subject=${subject}&body=${body}`;
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="w-20 h-20 rounded-full bg-red-50 dark:bg-red-950/30 flex items-center justify-center mb-6 ring-1 ring-red-200/60 dark:ring-red-800/40">
        <Lock className="w-9 h-9 text-red-400 dark:text-red-500" strokeWidth={1.5} />
      </div>

      <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100 mb-3 tracking-tight">Account Locked</h1>

      <p className="text-base text-slate-500 dark:text-slate-400 text-center leading-relaxed max-w-sm mb-4">
        Your account has been locked for security reasons.
      </p>
      <p className="text-sm text-slate-400 dark:text-slate-500 text-center leading-relaxed max-w-sm mb-10">
        All features have been temporarily restricted. Please reach out to our security team for next steps.
      </p>

      {sent ? (
        <div className="text-center mb-4">
          <p className="text-base font-medium text-green-600 dark:text-green-400 mb-1">Request sent successfully</p>
          <p className="text-sm text-slate-400 dark:text-slate-500">
            Our security team will review your account and follow up shortly.
          </p>
        </div>
      ) : (
        <button
          onClick={handleContactSecurity}
          disabled={sending}
          className="inline-flex items-center gap-2.5 px-10 py-4 rounded-xl bg-primary text-white font-medium text-base tracking-wide hover:bg-primary-hover transition-colors duration-200 shadow-sm mb-3 disabled:opacity-60"
        >
          <Mail className="w-5 h-5" />
          {sending ? 'Sending...' : 'Contact Security Team'}
        </button>
      )}

      <p className="text-sm text-slate-400 dark:text-slate-500">{SECURITY_EMAIL}</p>
    </>
  );
}

function LockedScreenContent() {
  const { subscription } = useSubscription();
  const status = subscription?.status || 'locked';

  if (status === 'trial_expired') {
    return <TrialExpiredScreen />;
  }

  if (status === 'pending_payment') {
    return <PendingPaymentScreen />;
  }

  return <SecurityLockedScreen />;
}

const LockedAccountScreen: React.FC = () => {
  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900">
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-lg mx-auto px-6">
        <div className="mb-16">
          <BrandLogo variant="auto" size="lg" />
        </div>
        <LockedScreenContent />
      </div>
      <div className="flex-shrink-0 pb-8 pt-4 text-center">
        <p className="text-[11px] text-slate-300 dark:text-slate-600 tracking-wide">
          Tropel Inc. &middot; St. John&rsquo;s, NL, Canada
        </p>
      </div>
    </div>
  );
};

export default LockedAccountScreen;
